'use client';

import {
  AttributeSide,
  AttributeSideDynamic,
} from '@eliadmin/core/src/components/categories/attributes/attribute';
import { AttributeCategory } from '@eliadmin/core/src/components/categories/attributes/attribute-category';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { LuX } from 'react-icons/lu';
import { MdTune } from 'react-icons/md';

const FilterSeparator = () => <Separator className="w-auto last-of-type:hidden" />;

export function CategoryFilterSheet() {
  const { isOpen, setIsOpen } = useDisclosure(false);

  const templateSettings = useGlobalStore(state => state.templateSettings);
  const categoryTree = useCategoryStore(state => state.categoryTree);
  const category = useCategoryStore(state => state.category);
  const { t } = useTranslations();

  const attributes = category.attributes?.filter(
    attribute => attribute.name && attribute.values?.length,
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild className="w-full sm:w-fit">
        <Button variant="outline" className="w-full sm:w-fit">
          <MdTune size={24} /> {t('text174')}
        </Button>
      </SheetTrigger>
      {isOpen && <div className={cn('fixed inset-0 z-[40] h-full w-full bg-black/30')} />}
      <SheetContent
        className={cn(
          'z-[43] flex h-screen w-full max-w-[640px] flex-col items-start justify-start overflow-y-scroll rounded-l-[16px] border-none px-0 py-0 shadow-none sm:max-w-[494px]',
          'hide-scroll-bar',
        )}
        showCloseIcon={false}
        hideOverlay={true}
      >
        <div className="flex w-full flex-col relative flex-1">
          <div className="flex w-full items-start justify-between px-4 py-6 sm:px-6">
            <h5 className="text-2xl font-medium tracking-tighter sm:text-[28px]">{t('text174')}</h5>
            <SheetClose>
              <LuX size={24} className="text-[#666666]" />
            </SheetClose>
          </div>

          <div
            className={cn(
              'flex h-full w-full flex-1 flex-col gap-4 px-4 sm:px-6 pb-8',
              'ea-category-filter-sheet__content',
            )}
          >
            <FilterSeparator />
            {/* price */}
            {!!templateSettings.category?.price_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text340')}
                  type="price"
                  attribute={{ type: 'status', type2: 'statuses' }}
                  order={templateSettings.category.price_in_filter.order}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__price') }}
                />
                <FilterSeparator />
              </>
            )}

            {/* categories */}
            {!!templateSettings.category?.tree_list?.enabled && !!categoryTree && (
              <AttributeCategory order={templateSettings.category.tree_list.order} />
            )}

            <FilterSeparator />

            {/* subcategories */}
            {!!templateSettings.category?.categories_in_filter?.enabled &&
              !!category.sub_categories?.length && (
                <>
                  <AttributeSide
                    name={t('text337')}
                    type="subCategory"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category.categories_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__subcategories') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* manufacturers */}
            {!!templateSettings.category?.manufacturers_in_filter?.enabled &&
              !!category.manufacturers?.length && (
                <>
                  <AttributeSide
                    name={t('text195')}
                    type="generic"
                    attribute={{ type: 'manufacturer', type2: 'manufacturers' }}
                    order={templateSettings.category.manufacturers_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__manufacturers') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* statuses */}
            {!!templateSettings.category?.statuses_in_filter?.enabled &&
              !!category.statuses?.length && (
                <>
                  <AttributeSide
                    name={t('text518')}
                    type="generic"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category?.categories_in_filter?.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__statuses') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* availabilities */}
            {!!templateSettings.category?.availability_in_filter?.enabled &&
              !!category.availabilities?.length && (
                <>
                  <AttributeSide
                    name={t('text339')}
                    type="generic"
                    attribute={{ type: 'availability', type2: 'availabilities' }}
                    order={templateSettings.category.availability_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__availabilities') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* attributes */}
            {!!templateSettings.category?.attributes_in_filter?.enabled && (
              // <div className={`order-${templateSettings.category.attributes_in_filter.order}`}>
              <>
                {attributes?.map((attribute, i) => (
                  <>
                    <AttributeSideDynamic
                      key={`attribute-${attribute.id ?? attribute.name}-${i}`}
                      name={attribute.name}
                      attribute={attribute as any}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__attributes') }}
                    />
                    <FilterSeparator />
                  </>
                ))}
              </>
            )}

            {!!templateSettings.category?.suppliers_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text718')}
                  type="generic"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers') }}
                  order={templateSettings.category.suppliers_in_filter.order}
                />
                <FilterSeparator />
              </>
            )}
            {!!templateSettings.category?.suppliers_location_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text728')}
                  type="supplierLocation"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers_location') }}
                  order={templateSettings.category?.suppliers_in_filter?.order}
                />
                <FilterSeparator />
              </>
            )}
          </div>

          <div
            className={cn(
              'sticky bottom-[58px] flex w-full flex-col justify-center gap-2 rounded-bl-[16px] bg-white px-6 py-4 sm:bottom-0',
              'ea-category-filter-sheet__button-container',
            )}
          >
            <SheetClose asChild className="w-full">
              <Button className="w-full">{t('text174')}</Button>
            </SheetClose>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
